/*  Talent Pool  (/app/recruit/pool)
 *
 *  Beheer van meerdere kandidaat-pools (bv. Cronos interne bench + algemene
 *  ATS + custom pools). Drie upload-modes in tab-navigatie:
 *
 *    1. 📄 CV-upload       — PDF/DOCX/TXT drag-drop → Claude Opus 4.8 extract
 *    2. 🔗 LinkedIn URLs   — plak URLs → Apollo enrich
 *    3. 📊 CSV-import      — spreadsheet met kolommen name/email/linkedin/…
 *
 *  Uploads landen in de GESELECTEERDE pool. Lijst-view onderaan toont de
 *  kandidaten van de actieve pool met filter (skills, seniority).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, API_BASE } from "@/lib/api";
import { PageHeader } from "../Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import {
    Users, Upload, FileText, Linkedin, Table, Loader2, Plus, X, Trash2,
    Calendar, Download, Search as SearchIcon, Sparkles, Filter, RefreshCw,
    FolderPlus, Layers, LayoutGrid, List as ListIcon, Mail, Send,
} from "lucide-react";
import CandidateAvatar from "@/components/CandidateAvatar";
import { useNavigate } from "react-router-dom";
import { LookalikeModal } from "@/components/recruit/LookalikeFinder";
import { UserSearch } from "lucide-react";

/* Vers geverifieerd e-mailadres (deep enrichment, Apify e-mail-modus) —
 * badge + outreach-suggestie blijven 7 dagen zichtbaar. */
const FRESH_EMAIL_WINDOW_MS = 7 * 24 * 3600 * 1000;
export const hasFreshEmail = (c) =>
    Boolean(c?.email && c?.email_verified_at
        && Date.now() - new Date(c.email_verified_at).getTime() < FRESH_EMAIL_WINDOW_MS);

export default function TalentPool() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [tab, setTab] = useState("browse");        // browse | cv | linkedin | csv
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [senFilter, setSenFilter] = useState([]);
    const [view, setView] = useState("cards");        // cards | list
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    // --- Multi-pool state ---
    const [pools, setPools] = useState([]);
    const [activePool, setActivePool] = useState("cronos");

    const loadPools = () => api.get("/talent-pool/pools")
        .then((r) => setPools(r.data || []))
        .catch(() => {});

    const load = () => {
        setLoading(true);
        api.get("/talent-pool", { params: { pool: activePool, limit: 300 } })
            .then((r) => { setRows(r.data || []); setLoading(false); })
            .catch(() => { setLoading(false); toast.error(i18n.t("pool.load_failed")); });
        loadPools();  // refresh counts
    };
    useEffect(() => { load(); }, [activePool]); // eslint-disable-line react-hooks/exhaustive-deps

    const activePoolName = pools.find((p) => p.key === activePool)?.name || activePool;

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const bulkDelete = async () => {
        if (!window.confirm(i18n.t("pool.bulk_delete_confirm", { n: selected.size }))) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/candidates/bulk-delete", { candidate_ids: [...selected].slice(0, 100) });
            toast.success(i18n.t("pool.bulk_deleted_toast", { n: data.deleted }));
            setSelected(new Set());
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pool.bulk_delete_failed")); }
        finally { setBulkBusy(false); }
    };

    const filtered = useMemo(() => rows.filter((c) => {
        if (senFilter.length > 0 && !senFilter.includes(c.seniority)) return false;
        if (query) {
            const q = query.toLowerCase();
            const hay = `${c.name} ${c.title} ${(c.skills || []).join(" ")} ${c.current_company || ""}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    }), [rows, query, senFilter]);

    return (
        <PageHeader
            title={t("pool.title")}
            subtitle={t("pool.subtitle")}
            actions={
                <div className="flex items-center gap-2 text-xs text-[var(--jr-text-3)]">
                    <Users size={13} className="text-[var(--jr-amber)]" />
                    <span className="font-display text-white text-lg">{rows.length}</span> {t("pool.in_pool")}
                </div>
            }
        >
            {/* Pool-selector — meerdere pools (Cronos / ATS / custom) */}
            <PoolSelector pools={pools} activePool={activePool}
                onSelect={(k) => setActivePool(k)} onChanged={loadPools} t={t} />

            {/* Upload tab bar */}
            <div className="flex items-center gap-2 flex-wrap border-b border-white/10 pb-3 mb-4">
                {[
                    { id: "browse", label: t("pool.tab_browse"), icon: Users },
                    { id: "cv", label: t("pool.tab_cv"), icon: FileText },
                    { id: "linkedin", label: t("pool.tab_linkedin"), icon: Linkedin },
                    { id: "csv", label: t("pool.tab_csv"), icon: Table },
                ].map((tb) => {
                    const active = tab === tb.id;
                    const Icon = tb.icon;
                    return (
                        <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`pool-tab-${tb.id}`}
                            className={`text-xs px-3 py-2 rounded-full border transition-colors inline-flex items-center gap-1.5 ${active
                                ? "bg-[var(--jr-amber)] text-black border-[var(--jr-amber)] font-medium"
                                : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                            <Icon size={12}/> {tb.label}
                        </button>
                    );
                })}
            </div>

            {tab === "cv" && <CVUploadTab onDone={load} pool={activePool} poolName={activePoolName} t={t} />}
            {tab === "linkedin" && <LinkedinUploadTab onDone={load} pool={activePool} poolName={activePoolName} t={t} />}
            {tab === "csv" && <CSVUploadTab onDone={load} pool={activePool} poolName={activePoolName} t={t} />}

            {tab === "browse" && (
                <>
                    {/* Filters */}
                    <div className="flex items-center gap-2 flex-wrap mb-4">
                        <div className="relative flex-1 max-w-md">
                            <SearchIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--jr-text-3)]" />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="pool-search"
                                placeholder={t("pool.search_placeholder")}
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-full pl-9 pr-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
                        </div>
                        <div className="flex items-center gap-1" data-testid="pool-seniority-filter">
                            <Filter size={11} className="text-[var(--jr-text-3)]" />
                            {["junior", "medior", "senior", "lead", "executive"].map((s) => {
                                const active = senFilter.includes(s);
                                return (
                                    <button key={s} onClick={() => setSenFilter((prev) => active ? prev.filter((x) => x !== s) : [...prev, s])}
                                        className={`text-[11px] px-2 py-0.5 rounded-full border ${active
                                            ? "bg-[var(--jr-amber)] text-black border-[var(--jr-amber)]"
                                            : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                                        {s}
                                    </button>
                                );
                            })}
                        </div>
                        {/* View toggle: cards | lijst */}
                        <div className="inline-flex rounded-full border border-white/10 p-0.5 ml-auto">
                            <button onClick={() => setView("cards")} data-testid="pool-view-cards"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] ${view === "cards" ? "bg-white/10 text-white" : "text-[var(--jr-text-3)] hover:text-white"}`}>
                                <LayoutGrid size={12} /> {t("pool.view_cards")}
                            </button>
                            <button onClick={() => setView("list")} data-testid="pool-view-list"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.14em] ${view === "list" ? "bg-white/10 text-white" : "text-[var(--jr-text-3)] hover:text-white"}`}>
                                <ListIcon size={12} /> {t("pool.view_list")}
                            </button>
                        </div>
                    </div>

                    {selected.size > 0 && (
                        <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-[var(--jr-amber)]/25 bg-[var(--jr-amber)]/[0.07] mb-4"
                            data-testid="pool-bulk-bar">
                            <span className="text-xs text-[var(--jr-text-2)]">{t("pool.selected_n", { n: selected.size })}</span>
                            <button onClick={bulkDelete} disabled={bulkBusy} data-testid="pool-bulk-delete"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 disabled:opacity-50">
                                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                {t("pool.bulk_delete", { n: selected.size })}
                            </button>
                            <button onClick={() => setSelected(new Set())} data-testid="pool-bulk-clear"
                                className="ml-auto text-xs text-[var(--jr-text-3)] hover:text-white">{t("common.cancel")}</button>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center text-sm text-[var(--jr-text-3)] py-12">
                            <Loader2 size={16} className="animate-spin inline mr-2" /> {t("pool.loading")}
                        </div>
                    ) : filtered.length === 0 ? (
                        <EmptyBrowseState onSwitchTab={setTab} t={t} />
                    ) : view === "list" ? (
                        <PoolListView rows={filtered} selected={selected} onToggleSelect={toggleSelect}
                            onSelectAll={() => setSelected(filtered.every((c) => selected.has(c.id)) ? new Set() : new Set(filtered.map((c) => c.id)))}
                            onOpen={(c) => nav(`/app/recruit/candidate/${c.id}`)} t={t} />
                    ) : (
                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="pool-grid">
                            {filtered.map((c) => <PoolCard key={c.id} c={c} onChanged={load} onOpen={() => nav(`/app/recruit/candidate/${c.id}`)} />)}
                        </div>
                    )}
                </>
            )}
        </PageHeader>
    );
}


/* ------------------------------------------------------------------ */
/*  Pool-selector — chips voor elke pool + inline create + delete       */
/* ------------------------------------------------------------------ */
function PoolSelector({ pools, activePool, onSelect, onChanged, t }) {
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(false);

    const createPool = async () => {
        if (!newName.trim()) return;
        setBusy(true);
        try {
            const r = await api.post("/talent-pool/pools", { name: newName.trim() });
            toast.success(t("pool.pool_created", { name: r.data.name }));
            setNewName(""); setCreating(false);
            onChanged?.();
            onSelect(r.data.key);
        } catch (e) { toast.error(e?.response?.data?.detail || t("pool.pool_create_failed")); }
        finally { setBusy(false); }
    };

    const deletePool = async (p) => {
        if (!confirm(t("pool.pool_delete_confirm", { name: p.name }))) return;
        try {
            await api.delete(`/talent-pool/pools/${p.id}`);
            toast.success(t("pool.pool_deleted", { name: p.name }));
            if (activePool === p.key) onSelect("cronos");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || t("pool.pool_delete_failed")); }
    };

    return (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="pool-selector">
            <Layers size={13} className="text-[var(--jr-text-3)]" />
            {pools.map((p) => {
                const active = p.key === activePool;
                return (
                    <span key={p.key} className="inline-flex items-center">
                        <button onClick={() => onSelect(p.key)} data-testid={`pool-select-${p.key}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${active
                                ? "bg-white text-black border-white font-medium"
                                : "border-white/12 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                            {p.name}
                            <span className={`text-[10px] font-mono px-1 rounded ${active ? "bg-black/10 text-black/70" : "bg-white/8 text-[var(--jr-text-3)]"}`}>
                                {p.candidate_count}
                            </span>
                            {!p.is_system && (
                                <X size={11} onClick={(e) => { e.stopPropagation(); deletePool(p); }}
                                    data-testid={`pool-delete-${p.key}`}
                                    className={`${active ? "text-black/50 hover:text-black" : "text-[var(--jr-text-3)] hover:text-[var(--jr-error)]"} cursor-pointer`} />
                            )}
                        </button>
                    </span>
                );
            })}
            {creating ? (
                <span className="inline-flex items-center gap-1.5">
                    <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") createPool(); if (e.key === "Escape") setCreating(false); }}
                        placeholder={t("pool.new_pool_name")}
                        data-testid="pool-new-name"
                        className="bg-[var(--jr-surface-2)] border border-white/15 rounded-full px-3 py-1.5 text-xs text-white focus:border-[var(--jr-amber)] outline-none w-40" />
                    <button onClick={createPool} disabled={busy || !newName.trim()} data-testid="pool-new-save"
                        className="text-xs px-3 py-1.5 rounded-full bg-[var(--jr-amber)] text-black font-medium hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                        {busy ? <Loader2 size={11} className="animate-spin" /> : t("common.save")}
                    </button>
                    <button onClick={() => { setCreating(false); setNewName(""); }}
                        className="text-xs text-[var(--jr-text-3)] hover:text-white">{t("common.cancel")}</button>
                </span>
            ) : (
                <button onClick={() => setCreating(true)} data-testid="pool-new-btn"
                    className="text-xs px-3 py-1.5 rounded-full border border-dashed border-white/20 text-[var(--jr-text-3)] hover:text-white hover:border-white/40 transition-colors inline-flex items-center gap-1.5">
                    <FolderPlus size={12} /> {t("pool.new_pool")}
                </button>
            )}
        </div>
    );
}


/* ------------------------------------------------------------------ */
/*  Lijst-view — excel-achtige tabel met multiselect                    */
/* ------------------------------------------------------------------ */
function PoolCheckbox({ checked, onClick, testid, label }) {
    return (
        <button onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testid} aria-label={label} title={label}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${checked ? "bg-[var(--jr-amber)] border-[var(--jr-amber)]" : "border-white/25 hover:border-white/50"}`}>
            {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
        </button>
    );
}

function PoolListView({ rows, selected, onToggleSelect, onSelectAll, onOpen, t }) {
    const allSelected = rows.length > 0 && rows.every((c) => selected.has(c.id));
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden" data-testid="pool-list">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left border-b border-white/8 bg-white/[0.02] text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)]">
                            <th className="pl-4 pr-1 py-2.5 w-8">
                                <PoolCheckbox checked={allSelected} onClick={onSelectAll}
                                    testid="pool-select-all" label={t("pool.select_all")} />
                            </th>
                            <th className="px-3 py-2.5">{t("pool.col_name")}</th>
                            <th className="px-3 py-2.5">{t("pool.col_title")}</th>
                            <th className="px-3 py-2.5">{t("pool.col_company")}</th>
                            <th className="px-3 py-2.5">{t("pool.col_location")}</th>
                            <th className="px-3 py-2.5">{t("pool.col_seniority")}</th>
                            <th className="px-3 py-2.5">{t("pool.col_skills")}</th>
                            <th className="px-3 py-2.5">{t("pool.col_available")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((c) => {
                            const isSel = selected.has(c.id);
                            return (
                                <tr key={c.id} data-testid={`pool-row-${c.id}`}
                                    onClick={() => onOpen(c)}
                                    className={`border-b border-white/5 cursor-pointer transition-colors ${isSel ? "bg-[var(--jr-amber)]/[0.06]" : "hover:bg-white/[0.02]"}`}>
                                    <td className="pl-4 pr-1 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
                                        <PoolCheckbox checked={isSel} onClick={() => onToggleSelect(c.id)}
                                            testid={`pool-select-cand-${c.id}`} label={t("pool.select_one")} />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <CandidateAvatar name={c.name} url={c.photo_url} size={24} />
                                            <span className="text-white font-medium truncate max-w-[180px]">{c.name}</span>
                                            {hasFreshEmail(c) && (
                                                <span title={`${t("pool.email_found")} · ${c.email}`}
                                                    data-testid={`pool-email-badge-${c.id}`}
                                                    className="inline-flex items-center justify-center w-4 h-4 rounded bg-[#22c55e]/15 text-[#22c55e] shrink-0">
                                                    <Mail size={9} />
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-2)] truncate max-w-[180px]" title={c.title}>{c.title || "—"}</td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-2)] truncate max-w-[140px]">{c.current_company || "—"}</td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-3)] truncate max-w-[120px]">{c.location || "—"}</td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]">{c.seniority || "—"}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                                            {(c.skills || []).slice(0, 3).map((s, i) => (
                                                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                                            ))}
                                            {(c.skills || []).length > 3 && <span className="text-[10px] text-[var(--jr-text-3)]">+{c.skills.length - 3}</span>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-[var(--jr-text-3)]">{c.available_from || "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


/* ------------------------------------------------------------------ */
function PoolCard({ c, onChanged, onOpen }) {
    const { t } = useTranslation();
    const nav = useNavigate();
    const [editing, setEditing] = useState(false);
    const [savingDate, setSavingDate] = useState(false);
    const [reparsing, setReparsing] = useState(false);
    const [outreachBusy, setOutreachBusy] = useState(false);
    const [availFrom, setAvailFrom] = useState(c.available_from || "");
    const [notes, setNotes] = useState(c.pool_notes || "");
    const [showLookalike, setShowLookalike] = useState(false);

    const startOutreach = async () => {
        setOutreachBusy(true);
        try {
            await api.post(`/talent-pool/${c.id}/start-outreach`, { channel: "email" });
            toast.success(i18n.t("pool.outreach_created", { name: c.name }), {
                action: { label: i18n.t("pool.view_outreach"), onClick: () => nav("/app/recruit/outreach") },
            });
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pool.outreach_failed")); }
        finally { setOutreachBusy(false); }
    };

    const save = async () => {
        setSavingDate(true);
        try {
            await api.patch(`/talent-pool/${c.id}`, { available_from: availFrom || null, pool_notes: notes || null });
            toast.success(i18n.t("pool.saved_ok"));
            setEditing(false);
            onChanged?.();
        } catch (e) { toast.error(i18n.t("pool.save_failed")); }
        finally { setSavingDate(false); }
    };

    const downloadCV = async () => {
        try {
            const r = await api.get(`/talent-pool/${c.id}/cv`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = c.cv_original_filename || `${c.name}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(i18n.t("pool.cv_dl_failed")); }
    };

    const reparseCV = async () => {
        if (!confirm(i18n.t("pool.reparse_confirm"))) return;
        setReparsing(true);
        try {
            const r = await api.post(`/talent-pool/${c.id}/reparse-cv`);
            toast.success(i18n.t("pool.reparsed_ok", { count: r.data.updated_fields.length }));
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pool.reparse_failed")); }
        finally { setReparsing(false); }
    };

    return (
        <div className="rounded-xl border border-white/10 bg-[var(--jr-surface)] p-4 hover:border-white/15 transition-colors" data-testid={`pool-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
                <button type="button" onClick={onOpen} data-testid={`pool-open-${c.id}`}
                    className="text-left flex items-start gap-3 min-w-0 flex-1 hover:opacity-90">
                    <CandidateAvatar name={c.name} url={c.photo_url} size={44} testid={`pool-avatar-${c.id}`} />
                    <div className="min-w-0 flex-1">
                        <div className="font-display text-base text-white truncate">{c.name}</div>
                        <div className="text-xs text-[var(--jr-text-2)] truncate">{c.title}</div>
                        <div className="text-[11px] text-[var(--jr-text-3)] truncate">{c.current_company || "Vrij"} · {c.location}</div>
                    </div>
                </button>
                <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-amber)]/15 text-[var(--jr-amber)] shrink-0">
                    {c.seniority}
                </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
                {(c.skills || []).slice(0, 6).map((s, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                ))}
            </div>
            {hasFreshEmail(c) && (
                <div className="mt-2.5 flex items-center justify-between gap-2 rounded-lg border border-[#22c55e]/25 bg-[#22c55e]/[0.06] px-2.5 py-1.5"
                    data-testid={`pool-email-found-${c.id}`} title={c.email}>
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#22c55e] inline-flex items-center gap-1.5 min-w-0">
                        <Mail size={10} className="shrink-0" />
                        <span className="truncate normal-case tracking-normal font-sans text-[11px]">{t("pool.email_found")}</span>
                    </span>
                    <button onClick={startOutreach} disabled={outreachBusy}
                        data-testid={`pool-start-outreach-${c.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#22c55e] text-black text-[10px] font-medium hover:bg-[#22c55e]/85 disabled:opacity-50 shrink-0">
                        {outreachBusy ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                        {t("pool.start_outreach")}
                    </button>
                </div>
            )}
            <div className="mt-3 pt-3 border-t border-white/8 space-y-1.5">
                {editing ? (
                    <div className="space-y-2">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1">{t("pool.available_from_label")}</label>
                            <input type="date" value={availFrom} onChange={(e) => setAvailFrom(e.target.value)}
                                data-testid={`pool-avail-${c.id}`}
                                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1 text-xs text-white focus:border-[var(--jr-amber)] outline-none" />
                        </div>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                            rows={2} placeholder={`${t("pool.notes_label")} (${t("pool.notes_placeholder")})`}
                            data-testid={`pool-notes-${c.id}`}
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded px-2 py-1 text-xs text-white focus:border-[var(--jr-amber)] outline-none" />
                        <div className="flex gap-1.5 justify-end">
                            <button onClick={() => setEditing(false)}
                                className="text-[10px] px-2 py-1 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">{t("common.cancel")}</button>
                            <button onClick={save} disabled={savingDate} data-testid={`pool-save-${c.id}`}
                                className="text-[10px] px-2.5 py-1 rounded-full bg-[var(--jr-amber)] text-black font-medium hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                                {savingDate ? <Loader2 size={9} className="animate-spin inline mr-1"/> : null} {t("common.save")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 flex-wrap text-[10px] text-[var(--jr-text-3)]">
                        {c.available_from ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--jr-success)]/10 text-[var(--jr-success)]">
                                <Calendar size={9}/> {t("pool.available_from")} {c.available_from}
                            </span>
                        ) : (
                            <span className="text-[var(--jr-text-3)]">{t("pool.no_date")}</span>
                        )}
                        <LinkedInButton url={c.linkedin_url} name={c.name} variant="icon" testid={`pool-linkedin-${c.id}`} className="!w-6 !h-6" resolve={{ type: "candidate", id: c.id }} />
                        <button onClick={() => setShowLookalike(true)} data-testid={`pool-lookalike-${c.id}`}
                            title={i18n.t("lookalike.subtitle_candidate", { name: c.name })}
                            className="text-[#a78bfa] hover:underline inline-flex items-center gap-0.5">
                            <UserSearch size={9} /> {i18n.t("lookalike.short_btn")}
                        </button>
                        {c.cv_storage_path && (
                            <>
                                <button onClick={downloadCV} data-testid={`pool-cv-${c.id}`}
                                    className="text-[var(--jr-info)] hover:underline inline-flex items-center gap-0.5">
                                    <Download size={9}/> CV
                                </button>
                                <button onClick={reparseCV} disabled={reparsing} data-testid={`pool-reparse-${c.id}`}
                                    title={t("pool.reparse_confirm").split("\n\n")[0]}
                                    className="text-[var(--jr-amber)] hover:underline inline-flex items-center gap-0.5 disabled:opacity-50">
                                    {reparsing ? <Loader2 size={9} className="animate-spin"/> : <RefreshCw size={9}/>}
                                    {t("pool.reparse")}
                                </button>
                            </>
                        )}
                        <button onClick={() => setEditing(true)} className="ml-auto text-[var(--jr-text-2)] hover:text-white underline">{t("common.edit")}</button>
                    </div>
                )}
            </div>
            <LookalikeModal open={showLookalike} onClose={() => setShowLookalike(false)} candidate={c} />
        </div>
    );
}


/* ------------------------------------------------------------------ */
function CVUploadTab({ onDone, pool, poolName, t }) {
    const [busyList, setBusyList] = useState([]);   // [{filename, status: 'uploading'|'ok'|'error', detail?}]
    const [availFrom, setAvailFrom] = useState("");
    const [notes, setNotes] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const process = async (files) => {
        const toProcess = Array.from(files).slice(0, 15);   // sane cap
        setBusyList(toProcess.map((f) => ({ filename: f.name, status: "uploading" })));
        for (let i = 0; i < toProcess.length; i++) {
            const f = toProcess[i];
            try {
                const form = new FormData();
                form.append("file", f);
                form.append("pool", pool);
                if (availFrom) form.append("available_from", availFrom);
                if (notes) form.append("pool_notes", notes);
                await api.post("/talent-pool/cv-upload", form, {
                    headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
                });
                setBusyList((prev) => prev.map((r, idx) => idx === i ? { ...r, status: "ok" } : r));
            } catch (e) {
                setBusyList((prev) => prev.map((r, idx) => idx === i ? {
                    ...r, status: "error", detail: e?.response?.data?.detail || e.message,
                } : r));
            }
        }
        onDone?.();
    };

    const onDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        process(e.dataTransfer.files);
    };

    return (
        <div className="max-w-2xl mx-auto" data-testid="cv-upload-tab">
            <UploadPoolHint poolName={poolName} t={t} />
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-1">{i18n.t("pool.avail_from_opt")}</label>
                    <input type="date" value={availFrom} onChange={(e) => setAvailFrom(e.target.value)} data-testid="cv-avail-from"
                        className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
                </div>
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-1">Pool notes (optioneel)</label>
                    <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cv-notes"
                        placeholder="bv. 'wil liefst BE-projecten'"
                        className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
                </div>
            </div>
            <label onDrop={onDrop} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                data-testid="cv-dropzone"
                className={`block rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragOver
                    ? "border-[var(--jr-amber)] bg-[var(--jr-amber)]/[0.06]"
                    : "border-white/15 hover:border-white/25 bg-[var(--jr-surface)]"}`}>
                <input type="file" multiple accept=".pdf,.docx,.doc,.txt" hidden
                    onChange={(e) => process(e.target.files)} data-testid="cv-file-input"/>
                <Upload size={28} className="mx-auto text-[var(--jr-amber)] mb-2"/>
                <p className="text-white font-medium">Sleep CV&apos;s hier of klik om te selecteren</p>
                <p className="text-xs text-[var(--jr-text-3)] mt-1">PDF, DOCX of TXT · max 8MB per bestand · max 15 tegelijk</p>
                <p className="text-[10px] text-[var(--jr-text-3)] mt-2">
                    <Sparkles size={9} className="inline text-[var(--jr-amber)]"/> Claude Opus 4.8 extraheert automatisch naam, functie, skills, ervaring & samenvatting.
                </p>
            </label>

            {busyList.length > 0 && (
                <div className="mt-4 space-y-1.5" data-testid="cv-upload-progress">
                    {busyList.map((r, i) => (
                        <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${r.status === "ok"
                            ? "border-[var(--jr-success)]/30 bg-[var(--jr-success)]/[0.06] text-[var(--jr-success)]"
                            : r.status === "error"
                            ? "border-[var(--jr-error)]/30 bg-[var(--jr-error)]/[0.06] text-[var(--jr-error)]"
                            : "border-white/10 bg-[var(--jr-surface-2)] text-[var(--jr-text-2)]"}`}>
                            {r.status === "uploading" && <Loader2 size={11} className="animate-spin"/>}
                            {r.status === "ok" && <span>✓</span>}
                            {r.status === "error" && <X size={11}/>}
                            <span className="flex-1 truncate">{r.filename}</span>
                            {r.detail && <span className="text-[10px] opacity-80 truncate max-w-xs">{r.detail}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


/* ------------------------------------------------------------------ */
function LinkedinUploadTab({ onDone, pool, poolName, t }) {
    const [urls, setUrls] = useState("");
    const [availFrom, setAvailFrom] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        const list = urls.split("\n").map((s) => s.trim()).filter(Boolean);
        if (list.length === 0) return toast.error("Plak minstens 1 LinkedIn URL.");
        setBusy(true);
        try {
            const r = await api.post("/talent-pool/linkedin-urls", {
                urls: list,
                available_from: availFrom || null,
                pool,
            });
            setResult(r.data);
            toast.success(i18n.t("pool.bulk_done", { created: r.data.created_count, skipped: r.data.skipped_count }));
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pool.bulk_failed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-3" data-testid="linkedin-tab">
            <UploadPoolHint poolName={poolName} t={t} />
            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-1">
                    LinkedIn URLs (één per lijn, max 50)
                </label>
                <textarea value={urls} onChange={(e) => setUrls(e.target.value)} rows={10}
                    placeholder={"https://linkedin.com/in/janedoe\nhttps://linkedin.com/in/johndoe"}
                    data-testid="linkedin-urls"
                    className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-[var(--jr-amber)] outline-none font-mono resize-y"/>
            </div>
            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-1">{i18n.t("pool.avail_from_all")}</label>
                <input type="date" value={availFrom} onChange={(e) => setAvailFrom(e.target.value)}
                    className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
            </div>
            <button onClick={submit} disabled={busy || !urls.trim()} data-testid="linkedin-submit"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--jr-amber)] text-black text-sm font-semibold hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Importeer & Verrijk via Apollo
            </button>
            {result && (
                <div className="text-xs text-[var(--jr-text-2)] rounded-lg border border-white/10 bg-[var(--jr-surface)] p-3">
                    ✓ {result.created_count} nieuwe kandidaten toegevoegd · {result.skipped_count} reeds bekend
                </div>
            )}
        </div>
    );
}


/* ------------------------------------------------------------------ */
function CSVUploadTab({ onDone, pool, poolName, t }) {
    const [file, setFile] = useState(null);
    const [availFrom, setAvailFrom] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async () => {
        if (!file) return toast.error(i18n.t("pool.pick_csv"));
        setBusy(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("pool", pool);
            if (availFrom) form.append("default_available_from", availFrom);
            const r = await api.post("/talent-pool/csv-upload", form, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
            });
            setResult(r.data);
            toast.success(i18n.t("pool.csv_done", { created: r.data.created_count, skipped: r.data.skipped_count }));
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pool.csv_failed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-3" data-testid="csv-tab">
            <UploadPoolHint poolName={poolName} t={t} />
            <div className="rounded-lg border border-white/10 bg-[var(--jr-surface)] p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2">{i18n.t("pool.csv_cols")}</div>
                <p className="text-xs text-[var(--jr-text-2)] font-mono">
                    Name · Email · LinkedIn · Roles · Skills · Years · Company · Location · Available From · Availability · Status · CV Filename
                </p>
                <p className="text-[10px] text-[var(--jr-text-3)] mt-1.5">{i18n.t("pool.import_hint")}</p>
            </div>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files[0])} data-testid="csv-file-input"
                className="w-full text-sm text-[var(--jr-text-2)] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[var(--jr-amber)] file:text-black file:font-medium file:cursor-pointer file:hover:bg-[var(--jr-amber)]/85"/>
            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] block mb-1">{i18n.t("pool.avail_from_default")}</label>
                <input type="date" value={availFrom} onChange={(e) => setAvailFrom(e.target.value)}
                    className="bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
            </div>
            <button onClick={submit} disabled={busy || !file} data-testid="csv-submit"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--jr-amber)] text-black text-sm font-semibold hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>} {i18n.t("pool.import_btn")}
            </button>
            {result && (
                <div className="text-xs text-[var(--jr-text-2)] rounded-lg border border-white/10 bg-[var(--jr-surface)] p-3 space-y-1">
                    <div>✓ {result.created_count} kandidaten geïmporteerd · {result.skipped_count} genegeerd.</div>
                    {result.skipped?.length > 0 && (
                        <details>
                            <summary className="text-[var(--jr-text-3)] cursor-pointer">Toon overgeslagen rijen</summary>
                            <ul className="mt-1 text-[10px] font-mono text-[var(--jr-text-3)] max-h-40 overflow-y-auto">
                                {result.skipped.slice(0, 20).map((s, i) => <li key={i}>rij {s.row}: {s.reason}</li>)}
                            </ul>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
}


/* ------------------------------------------------------------------ */
function UploadPoolHint({ poolName, t }) {
    return (
        <div className="mb-3 inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[var(--jr-amber)]/8 border border-[var(--jr-amber)]/25 text-[var(--jr-amber)]" data-testid="upload-pool-hint">
            <Layers size={11} /> {t("pool.upload_to_pool", { name: poolName })}
        </div>
    );
}


/* ------------------------------------------------------------------ */
function EmptyBrowseState({ onSwitchTab, t }) {
    return (
        <div className="rounded-xl border border-dashed border-white/12 p-10 text-center" data-testid="pool-empty">
            <Users size={32} className="mx-auto text-[var(--jr-text-3)] mb-3" />
            <h3 className="text-lg font-display text-white mb-1">{t("pool.empty_title")}</h3>
            <p className="text-sm text-[var(--jr-text-2)] max-w-md mx-auto mb-4">
                {t("pool.empty_body")}
            </p>
            <div className="flex items-center justify-center gap-2">
                <button onClick={() => onSwitchTab("cv")} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full bg-[var(--jr-amber)] text-black font-medium hover:bg-[var(--jr-amber)]/85">
                    <FileText size={13}/> {t("pool.tab_cv")}
                </button>
                <button onClick={() => onSwitchTab("linkedin")} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-white/15 text-[var(--jr-text-2)] hover:bg-white/5">
                    <Linkedin size={13}/> {t("pool.tab_linkedin")}
                </button>
                <button onClick={() => onSwitchTab("csv")} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full border border-white/15 text-[var(--jr-text-2)] hover:bg-white/5">
                    <Table size={13}/> {t("pool.tab_csv")}
                </button>
            </div>
        </div>
    );
}
